import React, { useState, useEffect, useMemo } from "react";
import {
  Button,
  Input,
  Chip,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Card,
  CardBody,
  CardHeader,
  Spinner,
  Select,
  SelectItem,
  Tooltip,
} from "@nextui-org/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faLink,
  faUnlink,
  faChevronDown,
  faChevronRight,
  faPlus,
  faTimes,
  faDownload,
  faRefresh,
  faTree,
} from "@fortawesome/free-solid-svg-icons";
import toast from "react-hot-toast";
import { getDataWithFilterUrl } from "../../controllers/APIController";
import {
  StandardPrimaryButton,
  StandardSecondaryButton,
  StandardDeleteButton,
  StandardCancelButton,
} from "../../components/StandardButtons";
import { useQuery, useQueryClient } from "@tanstack/react-query";
// Constants for data consistency
const DATA_TYPES = {
  SUSTAINABILITY_STANDARDS: "Sustainability Standards",
};

// Enhanced Tree Node Component with Policy Linking
const TreeNode = ({
  node,
  level = 0,
  policies,
  policyMappings,
  onLinkPolicies,
  onUnlinkPolicy,
  getLinkedPolicies,
}) => {
  const [isExpanded, setIsExpanded] = useState(level === 0);
  const [showPolicyModal, setShowPolicyModal] = useState(false);

  const hasChildren = node.children && node.children.length > 0;
  const nodeId = node.id || `${node.name}_${level}`;
  const linkedPolicies = getLinkedPolicies(nodeId);

  const getLevelIndent = (level) => level * 20;

  const handleLinkPolicies = (selectedPolicyIds) => {
    onLinkPolicies(nodeId, selectedPolicyIds);
    setShowPolicyModal(false);
  };

  const handleUnlinkPolicy = (policyId) => {
    onUnlinkPolicy(nodeId, policyId);
  };

  return (
    <div className="border border-neutral-200 rounded-xl bg-white mb-3 shadow-sm hover:shadow-md transition-all duration-200">
      <div className="p-4">
        <div className="flex items-center justify-between">
          <div
            className="flex items-center gap-3"
            style={{ marginLeft: `${getLevelIndent(level)}px` }}
          >
            {hasChildren && (
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="p-2 hover:bg-emerald-50 rounded-lg transition-colors group"
              >
                <FontAwesomeIcon
                  icon={isExpanded ? faChevronDown : faChevronRight}
                  className="w-4 h-4 text-neutral-500 group-hover:text-emerald-600 transition-colors"
                />
              </button>
            )}

            <div className="flex items-center gap-3">
              <span className="font-semibold text-neutral-900 text-sm">
                {node.name}
              </span>
              {node.code && (
                <Chip
                  size="sm"
                  variant="flat"
                  className="bg-neutral-100 text-neutral-700 border-neutral-200 text-xs font-medium"
                >
                  {node.code}
                </Chip>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3">
            {linkedPolicies.length > 0 && (
              <Chip
                size="sm"
                variant="flat"
                className="bg-emerald-100 text-emerald-700 border-emerald-200"
              >
                {linkedPolicies.length} policies
              </Chip>
            )}
            <StandardSecondaryButton
              size="sm"
              className="bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100"
              startContent={
                <FontAwesomeIcon icon={faLink} className="w-3 h-3" />
              }
              onClick={() => setShowPolicyModal(true)}
            >
              Link Policies
            </StandardSecondaryButton>
          </div>
        </div>

        {/* Display Linked Policies */}
        {linkedPolicies.length > 0 && (
          <div className="mt-4 ml-6">
            <div className="text-xs font-medium text-neutral-600 mb-3 uppercase tracking-wide">
              Linked Policies:
            </div>
            <div className="flex flex-wrap gap-2">
              {linkedPolicies.map((policy) => (
                <div
                  key={policy.id}
                  className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 hover:bg-emerald-100 transition-colors"
                >
                  <span className="text-sm font-medium text-emerald-800">
                    {policy.attributes?.Name}
                  </span>
                  <button
                    onClick={() => handleUnlinkPolicy(policy.id)}
                    className="text-emerald-600 hover:text-emerald-800 p-1 rounded hover:bg-emerald-200 transition-colors"
                  >
                    <FontAwesomeIcon icon={faTimes} className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Children */}
      {hasChildren && isExpanded && (
        <div className="ml-6 border-l-2 border-emerald-200 pl-6">
          {node.children.map((child, index) => (
            <TreeNode
              key={child.id || `${child.name}_${level + 1}_${index}`}
              node={child}
              level={level + 1}
              policies={policies}
              policyMappings={policyMappings}
              onLinkPolicies={onLinkPolicies}
              onUnlinkPolicy={onUnlinkPolicy}
              getLinkedPolicies={getLinkedPolicies}
            />
          ))}
        </div>
      )}

      {/* Policy Selection Modal */}
      <Modal
        isOpen={showPolicyModal}
        onOpenChange={setShowPolicyModal}
        size="2xl"
      >
        <ModalContent>
          <ModalHeader>
            <h3 className="text-lg font-semibold">
              Link Policies to: {node.name}
            </h3>
          </ModalHeader>
          <ModalBody>
            <PolicySelectionModal
              policies={policies}
              selectedPolicyIds={linkedPolicies.map((p) => p.id)}
              onConfirm={handleLinkPolicies}
              onCancel={() => setShowPolicyModal(false)}
            />
          </ModalBody>
        </ModalContent>
      </Modal>
    </div>
  );
};

// Policy Selection Modal Component
const PolicySelectionModal = ({
  policies,
  selectedPolicyIds,
  onConfirm,
  onCancel,
}) => {
  const [selectedPolicies, setSelectedPolicies] = useState(
    new Set(selectedPolicyIds)
  );
  const [searchQuery, setSearchQuery] = useState("");

  console.log("🔍 PolicySelectionModal received:", {
    policiesCount: policies?.length || 0,
    policies: policies,
    selectedPolicyIds: selectedPolicyIds,
    selectedPoliciesSize: selectedPolicies.size,
  });

  const filteredPolicies = useMemo(() => {
    const filtered = policies.filter(
      (policy) =>
        policy.attributes?.Name?.toLowerCase().includes(
          searchQuery.toLowerCase()
        ) ||
        policy.attributes?.Description?.toLowerCase().includes(
          searchQuery.toLowerCase()
        )
    );

    console.log("🔍 Filtered policies:", {
      searchQuery,
      totalPolicies: policies?.length || 0,
      filteredCount: filtered.length,
      filtered: filtered,
    });

    return filtered;
  }, [policies, searchQuery]);

  const handleTogglePolicy = (policyId) => {
    const newSelection = new Set(selectedPolicies);
    if (newSelection.has(policyId)) {
      newSelection.delete(policyId);
    } else {
      newSelection.add(policyId);
    }
    setSelectedPolicies(newSelection);
  };

  const handleConfirm = () => {
    onConfirm(Array.from(selectedPolicies));
  };

  return (
    <div className="space-y-6">
      <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-200">
        <p className="text-emerald-800 text-sm font-medium leading-relaxed">
          Select policies to link to this tree node. You can select multiple
          policies.
        </p>
      </div>

      <div>
        <Input
          placeholder="Search policies..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          startContent={
            <FontAwesomeIcon
              icon={faSearch}
              className="w-4 h-4 text-neutral-400"
            />
          }
          size="sm"
          classNames={{
            input: "text-sm",
            inputWrapper:
              "bg-neutral-50 border-neutral-200 hover:border-emerald-300 focus-within:border-emerald-500 focus-within:bg-white transition-colors",
          }}
        />
      </div>

      <div className="max-h-64 overflow-y-auto border border-neutral-200 rounded-xl p-4 bg-neutral-50">
        {policies && policies.length > 0 ? (
          filteredPolicies.length > 0 ? (
            <div className="space-y-3">
              {filteredPolicies.map((policy) => (
                <div
                  key={policy.id}
                  className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-all duration-200 ${
                    selectedPolicies.has(policy.id)
                      ? "border-emerald-300 bg-emerald-50 shadow-sm"
                      : "border-neutral-200 hover:border-emerald-300 hover:bg-white"
                  }`}
                  onClick={() => handleTogglePolicy(policy.id)}
                >
                  <input
                    type="checkbox"
                    checked={selectedPolicies.has(policy.id)}
                    onChange={() => handleTogglePolicy(policy.id)}
                    className="rounded border-neutral-300 text-emerald-600 focus:ring-emerald-500 mt-1"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-neutral-900 text-sm mb-1">
                      {policy.attributes?.Name}
                    </div>
                    <div className="text-xs text-neutral-600 leading-relaxed">
                      {policy.attributes?.Description}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="w-12 h-12 bg-neutral-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <FontAwesomeIcon
                  icon={faSearch}
                  className="w-5 h-5 text-neutral-400"
                />
              </div>
              <p className="text-sm font-medium text-neutral-600 mb-1">
                No policies match your search
              </p>
              <p className="text-xs text-neutral-400">
                Try a different search term
              </p>
            </div>
          )
        ) : (
          <div className="text-center py-8">
            <div className="w-12 h-12 bg-neutral-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <FontAwesomeIcon
                icon={faPlus}
                className="w-5 h-5 text-neutral-400"
              />
            </div>
            <p className="text-sm font-medium text-neutral-600 mb-1">
              No policies available
            </p>
            <p className="text-xs text-neutral-400">
              Create policies to link with standards
            </p>
          </div>
        )}
      </div>

      <div className="flex justify-between items-center pt-4 border-t border-neutral-200">
        <span className="text-sm font-medium text-neutral-600">
          {selectedPolicies.size} policies selected
        </span>
        <div className="flex gap-3">
          <StandardCancelButton
            size="sm"
            className="border-neutral-200 text-neutral-700"
            onPress={onCancel}
          >
            Cancel
          </StandardCancelButton>
          <StandardPrimaryButton size="sm" onPress={handleConfirm}>
            Link Selected Policies
          </StandardPrimaryButton>
        </div>
      </div>
    </div>
  );
};

// Enhanced Standards-Policies Mapping Component
const StandardsPoliciesMapping = ({ orgId, userId }) => {
  const [policies, setPolicies] = useState([]);
  const [standards, setStandards] = useState([]);
  const [selectedStandard, setSelectedStandard] = useState(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [policySearchQuery, setPolicySearchQuery] = useState("");
  const [policyMappings, setPolicyMappings] = useState({});
  const orgFilterUrl = `filters[Organization][id][$eqi]=${orgId}`;
  const clinetQuery = useQueryClient();

  // Fetch data
  const { data: policiesData, status:policyStatus } = useQuery({
    queryKey: ["policies", orgFilterUrl],
    queryFn: () => getDataWithFilterUrl("policies", orgFilterUrl),
  });
  const { data: allPoliciesData,status:allPolicyStatus } = useQuery({
    queryKey: ["policies", ""],
    queryFn: () => getDataWithFilterUrl("policies", ""),
  });
  const { data: reportingStandardsData ,status:reportingStatus} = useQuery({
    queryKey: ["responses", `filters[Metric_Id][$eqi]=Reporting Standards`],
    queryFn: () =>
      getDataWithFilterUrl(
        "responses",
        `filters[Metric_Id][$eqi]=Reporting Standards`
      ),
  });
  const { data: customStandardsData ,status:customStandardStatus } = useQuery({
    queryKey: ["responses", `filters[Metric_Id][$eqi]=Custom Standards`],
    queryFn: () =>
      getDataWithFilterUrl(
        "responses",
        `filters[Metric_Id][$eqi]=Custom Standards`
      ),
  });
  useEffect(() => {
    if (orgId) {
      let finalPoliciesData = policiesData;
      if (!policiesData?.data || policiesData.data.length === 0) {
        console.log(
          "No policies found with organization filter, trying to fetch all policies"
        );
        try {
         
          console.log("All policies data received:", {
            hasData: !!allPoliciesData?.data,
            dataLength: allPoliciesData?.data?.length || 0,
            policies: allPoliciesData?.data || [],
          });
          finalPoliciesData = allPoliciesData;
        } catch (fallbackError) {
          console.error("Fallback policies fetch failed:", fallbackError);
        }
      }

      // Combine all standards data
      const allStandards = [
        ...(reportingStandardsData?.data || []),
        ...(customStandardsData?.data || []),
      ];

      setPolicies(finalPoliciesData?.data || []);
      setStandards(allStandards);

      // Load existing policy mappings
      const savedMappings = localStorage.getItem(
        `enhanced-policy-mappings-${orgId}`
      );
      if (savedMappings) {
        setPolicyMappings(JSON.parse(savedMappings));
      }
    } else {
      console.warn("No orgId provided, skipping data fetch");
    }
  }, [
    customStandardsData,
    reportingStandardsData,
    policiesData,
    allPoliciesData,
    orgId,
  ]);

  // Filter standards based on search - use the correct field paths
  const filteredStandards = useMemo(() => {
    const filtered = standards.filter((standard) => {
      const standardName =
        standard.attributes?.JsonSchema?.flatData?.["Standard Name"] ||
        standard.attributes?.JsonSchema?.standards?.[0]?.name ||
        `Standard ${standard.id}`;

      const standardDescription =
        standard.attributes?.JsonSchema?.flatData?.["Description"] ||
        standard.attributes?.JsonSchema?.standards?.[0]?.description ||
        "";

      const matchesSearch =
        searchQuery === "" ||
        standardName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        standardDescription.toLowerCase().includes(searchQuery.toLowerCase());

      return matchesSearch;
    });

    return filtered;
  }, [standards, searchQuery]);

  // Filter policies based on search
  const filteredPolicies = useMemo(() => {
    return policies.filter((policy) => {
      const policyName = policy.attributes?.Name || `Policy ${policy.id}`;
      const policyDescription = policy.attributes?.Description || "";

      return (
        policySearchQuery === "" ||
        policyName.toLowerCase().includes(policySearchQuery.toLowerCase()) ||
        policyDescription
          .toLowerCase()
          .includes(policySearchQuery.toLowerCase())
      );
    });
  }, [policies, policySearchQuery]);

  // Get linked policies for a specific tree node
  const getLinkedPolicies = (nodeId) => {
    const policyIds = policyMappings[nodeId] || [];
    return policies.filter((policy) => policyIds.includes(policy.id));
  };

  // Link policies to a tree node
  const handleLinkPolicies = (nodeId, policyIds) => {
    const newMappings = { ...policyMappings };
    newMappings[nodeId] = policyIds;

    setPolicyMappings(newMappings);
    localStorage.setItem(
      `enhanced-policy-mappings-${orgId}`,
      JSON.stringify(newMappings)
    );

    toast.success(
      `Linked ${policyIds.length} policies to tree node successfully!`
    );
  };

  // Unlink a policy from a tree node
  const handleUnlinkPolicy = (nodeId, policyId) => {
    const newMappings = { ...policyMappings };
    if (newMappings[nodeId]) {
      newMappings[nodeId] = newMappings[nodeId].filter((id) => id !== policyId);
      if (newMappings[nodeId].length === 0) {
        delete newMappings[nodeId];
      }
    }

    setPolicyMappings(newMappings);
    localStorage.setItem(
      `enhanced-policy-mappings-${orgId}`,
      JSON.stringify(newMappings)
    );

    toast.success("Policy unlinked successfully!");
  };

  // Get standard tree structure - handle both reporting and custom standards
  const getStandardTree = (standard) => {
    try {
      // Handle the JsonSchema structure from responses table
      if (standard.attributes?.JsonSchema?.flatData?.["Json Structure"]) {
        const tree = JSON.parse(
          standard.attributes.JsonSchema.flatData["Json Structure"]
        );
        return tree;
      }

      // Handle legacy structure
      if (standard.attributes?.JsonSchema?.standards) {
        return standard.attributes.JsonSchema;
      }

      return null;
    } catch (error) {
      console.error("Error parsing standard structure:", error);
      return null;
    }
  };

  if (
    policyStatus == "pending" ||
    reportingStatus == "pending" ||
    customStandardStatus == "pending" 

  ) {
    return (
      <div className="flex items-center justify-center p-8">
        <Spinner size="lg" color="primary" />
        <span className="ml-3 text-neutral-600">
          Loading standards and policies...
        </span>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Page Header */}
      <div className="bg-gradient-to-r from-white to-emerald-50 border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                <FontAwesomeIcon
                  icon={faLink}
                  className="w-6 h-6 text-emerald-600"
                />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-neutral-900 mb-2">
                  Standards-Policies Mapping
                </h1>
                <p className="text-neutral-600 text-base leading-relaxed">
                  Link your policies to standards tree nodes for compliance
                  mapping
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Status Display */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-center justify-center gap-8 text-sm">
          <div className="flex items-center gap-3">
            <div className="w-3 h-3 bg-emerald-500 rounded-full"></div>
            <span className="text-neutral-700 font-medium">
              {policies?.length || 0} policies available
            </span>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-3 h-3 bg-emerald-500 rounded-full"></div>
            <span className="text-neutral-700 font-medium">
              {standards?.length || 0} standards loaded
            </span>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
            <span className="text-neutral-700 font-medium">
              {Object.keys(policyMappings).length} nodes mapped
            </span>
          </div>
        </div>
      </div>

      {/* Import/Export Controls */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-center justify-center gap-4">
          <StandardSecondaryButton
            size="sm"
            className="bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100"
            startContent={<FontAwesomeIcon icon={faPlus} className="w-3 h-3" />}
            onClick={() => {
              const input = document.createElement("input");
              input.type = "file";
              input.accept = ".json";
              input.onchange = (e) => {
                const file = e.target.files[0];
                if (file) {
                  const reader = new FileReader();
                  reader.onload = (e) => {
                    try {
                      const importedMappings = JSON.parse(e.target.result);
                      setPolicyMappings(importedMappings);
                      localStorage.setItem(
                        `enhanced-policy-mappings-${orgId}`,
                        JSON.stringify(importedMappings)
                      );
                      toast.success("Policy mappings imported successfully!");
                    } catch (error) {
                      toast.error(
                        "Invalid file format. Please use a valid JSON file."
                      );
                    }
                  };
                  reader.readAsText(file);
                }
              };
              input.click();
            }}
          >
            Import Mappings
          </StandardSecondaryButton>

          <StandardSecondaryButton
            size="sm"
            className="bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100"
            startContent={
              <FontAwesomeIcon icon={faDownload} className="w-3 h-3" />
            }
            onClick={() => {
              const dataStr = JSON.stringify(policyMappings, null, 2);
              const dataBlob = new Blob([dataStr], {
                type: "application/json",
              });
              const url = URL.createObjectURL(dataBlob);
              const link = document.createElement("a");
              link.href = url;
              link.download = `policy-mappings-${orgId}-${
                new Date().toISOString().split("T")[0]
              }.json`;
              link.click();
              URL.revokeObjectURL(url);
              toast.success("Policy mappings exported successfully!");
            }}
          >
            Export Mappings
          </StandardSecondaryButton>

          <StandardDeleteButton
            size="sm"
            startContent={
              <FontAwesomeIcon icon={faTimes} className="w-3 h-3" />
            }
            onClick={() => {
              if (
                window.confirm(
                  "Are you sure you want to clear all policy mappings? This action cannot be undone."
                )
              ) {
                setPolicyMappings({});
                localStorage.removeItem(`enhanced-policy-mappings-${orgId}`);
                toast.success("All policy mappings cleared!");
              }
            }}
          >
            Clear All
          </StandardDeleteButton>

          {/* Test button to fetch policies without organization filter */}
          <StandardSecondaryButton
            size="sm"
            startContent={
              <FontAwesomeIcon icon={faRefresh} className="w-3 h-3" />
            }
            onClick={async () => {
              try {
                clinetQuery.invalidateQueries({ queryKey: ["policies", ""] });
                if (allPoliciesData?.data && allPoliciesData.data.length > 0) {
                  setPolicies(allPoliciesData.data);
                  toast.success(
                    `Loaded ${allPoliciesData.data.length} policies directly from API`
                  );
                } else {
                  toast.error("No policies found in direct API call");
                }
              } catch (error) {
                console.error("🧪 Direct policies fetch failed:", error);
                toast.error("Failed to fetch policies directly");
              }
            }}
          >
            Test API
          </StandardSecondaryButton>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex gap-8 min-h-[70vh]">
          {/* Left Panel - Standards List */}
          <div className="w-96 bg-white border border-neutral-200 rounded-xl shadow-sm p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-xl font-semibold text-neutral-900 mb-1">
                  Standards
                </h3>
                <p className="text-sm text-neutral-500">
                  {filteredStandards.length} of {standards.length} standards
                </p>
              </div>
              <Chip
                size="sm"
                variant="flat"
                className="bg-emerald-50 text-emerald-700 border-emerald-200"
              >
                {standards.length} total
              </Chip>
            </div>

            {/* Standards Search */}
            <div className="mb-6">
              <Input
                placeholder="Search standards..."
                size="sm"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                startContent={
                  <FontAwesomeIcon
                    icon={faSearch}
                    className="w-4 h-4 text-neutral-400"
                  />
                }
                className="w-full"
                classNames={{
                  input: "text-sm",
                  inputWrapper:
                    "bg-neutral-50 border-neutral-200 hover:border-emerald-300 focus-within:border-emerald-500 focus-within:bg-white transition-colors",
                }}
              />
            </div>

            {/* Standards List */}
            <div className="space-y-3 max-h-80 overflow-y-auto pr-2">
              {filteredStandards.length === 0 ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-neutral-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <FontAwesomeIcon
                      icon={faSearch}
                      className="w-6 h-6 text-neutral-400"
                    />
                  </div>
                  <p className="text-sm font-medium text-neutral-600 mb-1">
                    No standards found
                  </p>
                  <p className="text-xs text-neutral-400">
                    Create standards to get started
                  </p>
                </div>
              ) : (
                filteredStandards.map((standard) => {
                  const standardName =
                    standard.attributes?.JsonSchema?.flatData?.[
                      "Standard Name"
                    ] ||
                    standard.attributes?.JsonSchema?.standards?.[0]?.name ||
                    `Standard ${standard.id}`;

                  return (
                    <div
                      key={standard.id}
                      className={`p-4 border rounded-xl cursor-pointer transition-all duration-200 hover:shadow-md ${
                        selectedStandard?.id === standard.id
                          ? "border-emerald-500 bg-emerald-50 shadow-md ring-2 ring-emerald-100"
                          : "border-neutral-200 hover:border-emerald-300 hover:bg-neutral-50"
                      }`}
                      onClick={() => setSelectedStandard(standard)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <h5 className="font-semibold text-neutral-900 text-sm mb-2 leading-tight">
                            {standardName}
                          </h5>
                          <p className="text-xs text-neutral-600 leading-relaxed mb-3">
                            {standard.attributes?.JsonSchema?.flatData?.[
                              "Description"
                            ] ||
                              standard.attributes?.JsonSchema?.standards?.[0]
                                ?.description ||
                              "No description available"}
                          </p>
                          <div className="flex items-center gap-2">
                            <Chip
                              size="sm"
                              variant="flat"
                              className="bg-emerald-100 text-emerald-700 border-emerald-200 text-xs font-medium"
                            >
                              Has Structure
                            </Chip>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Center Panel - Policies */}
          <div className="w-96 bg-white border border-neutral-200 rounded-xl shadow-sm p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-xl font-semibold text-neutral-900 mb-1">
                  Policies
                </h3>
                <p className="text-sm text-neutral-500">
                  {filteredPolicies.length} of {policies.length} policies
                  {policySearchQuery && (
                    <span className="text-emerald-600 font-medium">
                      [Filtered from {policies.length} total]
                    </span>
                  )}
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="solid"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white border-0 shadow-sm"
                  startContent={
                    <FontAwesomeIcon icon={faPlus} className="w-3 h-3" />
                  }
                  onClick={() => {
                    /* TODO: Add policy creation */
                  }}
                >
                  New Policy
                </Button>
              </div>
            </div>

            {/* Policy Search */}
            <div className="mb-6">
              <Input
                placeholder="Search policies..."
                size="sm"
                value={policySearchQuery}
                onChange={(e) => setPolicySearchQuery(e.target.value)}
                startContent={
                  <FontAwesomeIcon
                    icon={faSearch}
                    className="w-4 h-4 text-neutral-400"
                  />
                }
                className="w-full"
                classNames={{
                  input: "text-sm",
                  inputWrapper:
                    "bg-neutral-50 border-neutral-200 hover:border-emerald-300 focus-within:border-emerald-500 focus-within:bg-white transition-colors",
                }}
              />
            </div>

            {/* Policy List */}
            <div className="space-y-3 max-h-80 overflow-y-auto pr-2">
              {filteredPolicies.length === 0 ? (
                <div className="text-center py-8">
                  {policies.length === 0 ? (
                    <>
                      <div className="w-16 h-16 bg-neutral-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <FontAwesomeIcon
                          icon={faPlus}
                          className="w-6 h-6 text-neutral-400"
                        />
                      </div>
                      <p className="text-sm font-medium text-neutral-600 mb-1">
                        No policies found
                      </p>
                      <p className="text-xs text-neutral-400">
                        Create policies to link with standards
                      </p>
                    </>
                  ) : (
                    <>
                      <div className="w-16 h-16 bg-neutral-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <FontAwesomeIcon
                          icon={faSearch}
                          className="w-6 h-6 text-neutral-400"
                        />
                      </div>
                      <p className="text-sm font-medium text-neutral-600 mb-1">
                        No policies match your search
                      </p>
                      <p className="text-xs text-neutral-400">
                        Try a different search term
                      </p>
                    </>
                  )}
                </div>
              ) : (
                filteredPolicies.map((policy) => (
                  <div
                    key={policy.id}
                    className="p-4 border border-neutral-200 rounded-xl hover:border-emerald-300 hover:bg-neutral-50 hover:shadow-md transition-all duration-200"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <h5 className="font-semibold text-neutral-900 text-sm mb-2 leading-tight">
                          {policy.attributes?.Name || `Policy ${policy.id}`}
                        </h5>
                        <p className="text-xs text-neutral-600 leading-relaxed mb-3">
                          {policy.attributes?.Description || "No description"}
                        </p>
                        <div className="flex items-center gap-2">
                          <Chip
                            size="sm"
                            variant="flat"
                            className="bg-emerald-100 text-emerald-700 border-emerald-200 text-xs font-medium"
                          >
                            Available
                          </Chip>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Right Panel - Tree Structure */}
        <div className="flex-1">
          {selectedStandard ? (
            <div className="bg-white border border-neutral-200 rounded-xl shadow-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-xl font-semibold text-neutral-900 mb-2">
                    {selectedStandard.attributes?.JsonSchema?.flatData?.[
                      "Standard Name"
                    ] ||
                      selectedStandard.attributes?.JsonSchema?.standards?.[0]
                        ?.name ||
                      `Standard ${selectedStandard.id}`}
                  </h3>
                  <p className="text-sm text-neutral-600 leading-relaxed">
                    {selectedStandard.attributes?.JsonSchema?.flatData?.[
                      "Description"
                    ] ||
                      selectedStandard.attributes?.JsonSchema?.standards?.[0]
                        ?.description ||
                      "No description available"}
                  </p>
                </div>
                <Chip
                  size="sm"
                  variant="flat"
                  className="bg-emerald-100 text-emerald-700 border-emerald-200"
                >
                  {Object.keys(policyMappings).length} nodes mapped
                </Chip>
              </div>

              <div className="border-t border-neutral-200 pt-6">
                <TreeStructureDisplay
                  standard={selectedStandard}
                  policies={policies}
                  policyMappings={policyMappings}
                  onLinkPolicies={handleLinkPolicies}
                  onUnlinkPolicy={handleUnlinkPolicy}
                  getLinkedPolicies={getLinkedPolicies}
                />
              </div>
            </div>
          ) : (
            <div className="bg-white border border-neutral-200 rounded-xl shadow-sm p-12 text-center">
              <div className="w-20 h-20 bg-neutral-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <FontAwesomeIcon
                  icon={faSearch}
                  className="w-10 h-10 text-neutral-300"
                />
              </div>
              <h3 className="text-xl font-semibold text-neutral-900 mb-3">
                Select a Standard
              </h3>
              <p className="text-neutral-600 text-base leading-relaxed max-w-md mx-auto">
                Choose a standard from the left sidebar to view its tree
                structure and link policies
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Tree Structure Display Component
const TreeStructureDisplay = ({
  standard,
  policies,
  policyMappings,
  onLinkPolicies,
  onUnlinkPolicy,
  getLinkedPolicies,
}) => {
  const getStandardTree = (standard) => {
    try {
      if (standard.attributes?.JsonSchema?.flatData?.["Json Structure"]) {
        return JSON.parse(
          standard.attributes.JsonSchema.flatData["Json Structure"]
        );
      }
      return null;
    } catch (error) {
      return null;
    }
  };

  const tree = getStandardTree(standard);

  if (!tree) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 bg-neutral-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <FontAwesomeIcon icon={faTree} className="w-8 h-8 text-neutral-400" />
        </div>
        <p className="text-sm font-medium text-neutral-600 mb-1">
          No tree structure available
        </p>
        <p className="text-xs text-neutral-400">
          This standard doesn't have a defined structure
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
          <FontAwesomeIcon icon={faTree} className="w-4 h-4 text-emerald-600" />
        </div>
        <h4 className="text-lg font-semibold text-neutral-900">
          Tree Structure
        </h4>
      </div>
      <TreeNode
        node={tree}
        level={0}
        policies={policies}
        policyMappings={policyMappings}
        onLinkPolicies={onLinkPolicies}
        onUnlinkPolicy={onUnlinkPolicy}
        getLinkedPolicies={getLinkedPolicies}
      />
    </div>
  );
};

export default StandardsPoliciesMapping;
